import React from 'react'
import { Lock } from 'lucide-react'
import { Badge } from '@/components/ui'
import type { OrganizationSubscription } from '@/types/organization.types'

const hexColorRegex = /^#([0-9A-Fa-f]{3}){1,2}$/

export interface OrganizationHeaderProps {
  name: string
  status: string
  subscription: OrganizationSubscription | undefined
  /** Billable seats, counted live by the server (`organization.seats.count`). */
  seatCount: number | undefined
  primaryColor: string | undefined
  canUpdate: boolean
}

interface FactProps {
  label: string
  children: React.ReactNode
}

function Fact({ label, children }: FactProps): React.ReactElement {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
        {children}
      </dd>
    </div>
  )
}

/**
 * Compact identity strip above the settings card. Replaces the previous hero
 * banner: same facts, one row, no decorative gradient.
 */
export function OrganizationHeader({
  name,
  status,
  subscription,
  seatCount,
  primaryColor,
  canUpdate,
}: OrganizationHeaderProps): React.ReactElement {
  const swatch = primaryColor && hexColorRegex.test(primaryColor) ? primaryColor : null

  return (
    <header className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 pb-5">
      <div className="flex min-w-0 items-center gap-3">
        <h1 className="truncate text-xl font-semibold tracking-[-0.015em] text-gray-900 dark:text-gray-100">
          {name || 'Organization'}
        </h1>
        <Badge
          variant={status === 'active' ? 'success' : status === 'suspended' ? 'warning' : 'neutral'}
        >
          {status || 'active'}
        </Badge>
        {!canUpdate && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            <Lock className="h-3 w-3" strokeWidth={2} aria-hidden />
            Read only
          </span>
        )}
      </div>

      <dl className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <Fact label="Plan">
          <span className="capitalize">{subscription?.plan ?? 'No plan'}</span>
        </Fact>
        {/* Seats are billed per head and never capped, so no "of N" ceiling. */}
        <Fact label="Seats">
          {typeof seatCount === 'number' ? (
            <span
              className="tabular-nums"
              title="Billable seats: active members who can sign in, admins included"
            >
              {seatCount}
            </span>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">Unavailable</span>
          )}
        </Fact>
        <Fact label="Accent">
          <span className="flex items-center gap-2">
            <span
              className="h-3.5 w-3.5 shrink-0 rounded-full shadow-[inset_0_0_0_1px_oklch(0_0_0/0.12)]"
              style={{ backgroundColor: swatch ?? 'transparent' }}
              aria-hidden
            />
            <span className="font-mono text-[13px] uppercase">{swatch ?? 'Not set'}</span>
          </span>
        </Fact>
      </dl>
    </header>
  )
}

OrganizationHeader.displayName = 'OrganizationHeader'
